/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import LanguageMenu from '../TopbarDesktop/CustomLinksMenu/LanguageMenu/LanguageMenu';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    inboxTo,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
    onUpdateProfile,
    onManageDisableScrolling,
  } = props;

  const user = ensureCurrentUser(currentUser);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const createListingsLinkMaybe = showCreateListingsLink && currentUser ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage
          id="TopbarMobileMenu.signupOrLogin"
          values={{ lineBreak: <br />, signup, login }}
        />
      </span>
    );

    const isDutch = (Intl?.locale || '').toLowerCase().startsWith('nl');

    return (
      <nav className={css.root}>

        <div className={css.content}>
          {/* menu links */}
          <div className={css.accountLinksWrapper}>
            <NamedLink name="SearchPage" className={css.navigationLink}>
              <span className={css.navigationLinkLabel}>
                <FormattedMessage id="TopbarDesktop.shopText" />
              </span>
            </NamedLink>

            <NamedLink name="NewListingPage" className={css.navigationLink}>
              <span className={css.navigationLinkLabel}>
                <FormattedMessage id="TopbarDesktop.SellText" />
              </span>
            </NamedLink>

            <NamedLink name="CMSPage" params={{ pageId: isDutch ? 'about-nl' : 'about' }} className={css.navigationLink}>
              <span className={css.navigationLinkLabel}>
                <FormattedMessage id="TopbarDesktop.aboutText" />
              </span>
            </NamedLink>

            <NamedLink name="CMSPage" params={{ pageId: isDutch ? 'how-it-work-nl' : 'how-it-work' }} className={css.navigationLink}>
              <span className={css.navigationLinkLabel}>
                <FormattedMessage id="TopbarDesktop.how-it-works-text" />
              </span>
            </NamedLink>

            <NamedLink name="CMSPage" params={{ pageId: 'contact-us' }} className={css.navigationLink}>
              <span className={css.navigationLinkLabel}>
                <FormattedMessage id="TopbarDesktop.contactText" />
              </span>
            </NamedLink>
          </div>
          <div className={css.customLinksWrapper}>{extraLinks}</div>
          {/* <div className={css.spacer} /> */}
        </div>

        <div className={css.footer}>
          {signup}

          {login}

          {/* {signupOrLogin} */}
          {/* <div className={css.authenticationGreeting}>
              <FormattedMessage
                id="TopbarMobileMenu.unauthorizedGreeting"
                values={{ lineBreak: <br />, signupOrLogin }}
              />
            </div> */}

          <LanguageMenu
            currentPage={currentPage}
            onManageDisableScrolling={onManageDisableScrolling}
            currentUser={currentUser}
            onUpdateProfile={onUpdateProfile}
          />



          {/* {createListingsLinkMaybe} */}

        </div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes?.profile?.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const isAdmin = user?.attributes?.profile?.publicData?.isAdmin === true;

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
    </NamedLink>
  ) : null;


  const isDutch = (Intl?.locale || '').toLowerCase().startsWith('nl');

  return (
    <div className={css.root}>

      {/* <AvatarLarge className={css.avatar} user={currentUser} /> */}
      <div className={css.content}>
        {/* <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span> */}

        <div className={css.accountLinksWrapper}>

          {/* menu links */}
          <>
            <NamedLink name="SearchPage" className={css.navigationLink}>
              <span className={css.navigationLinkLabel}>
                <FormattedMessage id="TopbarDesktop.shopText" />
              </span>
            </NamedLink>

            <NamedLink name="NewListingPage" className={css.navigationLink}>
              <span className={css.navigationLinkLabel}>
                <FormattedMessage id="TopbarDesktop.SellText" />
              </span>
            </NamedLink>

            <NamedLink name="CMSPage" params={{ pageId: isDutch ? 'about-nl' : 'about' }} className={css.navigationLink}>
              <span className={css.navigationLinkLabel}>
                <FormattedMessage id="TopbarDesktop.aboutText" />
              </span>
            </NamedLink>

            <NamedLink
              className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
              name="InboxPage"
              params={{ tab: inboxTab }}
              to={inboxTo || undefined}
            >
              <FormattedMessage id="TopbarMobileMenu.inboxLink" />
              {notificationCountBadge}
            </NamedLink>

            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
            </NamedLink>


            {manageListingsLinkMaybe}
            {isAdmin ? (
              <NamedLink
                className={classNames(css.navigationLink, currentPageClass('AdminPanelPage'))}
                name="AdminPanelPage"
              >
                Admin Panel
              </NamedLink>
            ) : null}
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
            </NamedLink>

            <NamedLink name="CMSPage" params={{ pageId: isDutch ? 'how-it-work-nl' : 'how-it-work' }} className={css.navigationLink}>
              <span className={css.navigationLinkLabel}>
                <FormattedMessage id="TopbarDesktop.how-it-works-text" />
              </span>
            </NamedLink>

            <NamedLink name="CMSPage" params={{ pageId: 'contact-us' }} className={css.navigationLink}>
              <span className={css.navigationLinkLabel}>
                <FormattedMessage id="TopbarDesktop.contactText" />
              </span>
            </NamedLink>

          </>

        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div>
      </div>


      <div>
        {createListingsLinkMaybe}
        <div className={css.footerLinks}>
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarMobileMenu.logoutLink" />
          </InlineTextButton>


          <LanguageMenu
            currentPage={currentPage}
            currentUser={currentUser}
            onUpdateProfile={onUpdateProfile}
            onManageDisableScrolling={onManageDisableScrolling}
          />
        </div>
      </div>
    </div>
  );
};

export default TopbarMobileMenu;
